import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back, sensei.");
      const to = loc.state?.from?.pathname || "/";
      nav(to, { replace: true });
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0f0f10",
            border: "1px solid #27272a",
            borderRadius: 0,
            color: "white",
          },
        }}
      />

      {/* Left panel — hero */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-zinc-800 overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1614583225154-5fcdda07019e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "grayscale(0.5) contrast(1.1)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/50 to-[color:var(--mecha-red)]/20" />

        <div className="relative">
          <div className="font-mono text-xs tracking-[0.3em] text-zinc-400">
            // POS SYSTEM · ISSUED 2026
          </div>
        </div>

        <div className="relative">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_otaku-checkout/artifacts/lnjvv7pq_Yellow%20Black%20Brush%20Streetwear%20Brand%20Logo%282%29.png"
            alt="Swagg Squad"
            className="w-40 h-40 object-contain mb-6 bg-white p-2"
          />
          <div className="font-display text-6xl xl:text-7xl leading-[0.9]">
            SWAGG
            <br />
            <span className="text-[color:var(--mecha-red)]">SQUAD</span>
          </div>
          <div className="mt-6 max-w-md text-zinc-300 text-sm leading-relaxed">
            Command center for anime figures, manga & collectibles. Ship faster.
            Sell smarter. Own the culture.
          </div>
          <div className="mt-8 flex gap-3 font-mono text-[10px] tracking-[0.3em] text-zinc-500">
            <span>▲ INVENTORY</span>
            <span>▲ ORDERS</span>
            <span>▲ SHIPPING</span>
          </div>
        </div>

        <div className="relative font-mono text-[10px] tracking-[0.3em] text-zinc-500">
          NEO-TOKYO / MUMBAI TERMINAL
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden w-14 h-14 bg-white p-1 mb-6">
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_otaku-checkout/artifacts/lnjvv7pq_Yellow%20Black%20Brush%20Streetwear%20Brand%20Logo%282%29.png"
              alt="Swagg Squad"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)] mb-3">
            // ADMIN ACCESS
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-none">
            SIGN IN
          </h1>
          <p className="text-zinc-400 mt-3 text-sm">
            Authenticate to access your POS terminal.
          </p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
                EMAIL
              </label>
              <input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 bg-zinc-950 border border-zinc-800 px-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)] transition-colors"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-2">
                PASSWORD
              </label>
              <input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full h-12 bg-zinc-950 border border-zinc-800 px-4 text-white placeholder-zinc-600 focus:outline-none focus:border-[color:var(--mecha-red)] transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="border border-[color:var(--mecha-red)] bg-[color:var(--mecha-red)]/10 px-4 py-3 text-sm text-[color:var(--mecha-red-hover)]"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="btn-brutal w-full h-12 bg-[color:var(--mecha-red)] text-white font-bold tracking-widest uppercase text-sm hover:bg-[color:var(--mecha-red-hover)] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter Terminal"}
            </button>
          </form>

          <div className="mt-10 font-mono text-[10px] tracking-[0.3em] text-zinc-600">
            // OTAKU VAULT · SECURE SESSION
          </div>
        </div>
      </div>
    </div>
  );
}
